import {
    transferSettingsOfObject,
    addFlex,
    determineTableType,
    addFragments,
    convertActionKey,
    importHeaderActions
} from '../common/index';
import type { ImportALPV2Parameters } from '@sap/ux-specification-types';
import { DefinitionName, ManifestSection, PageTypeV2, v2 } from '@sap/ux-specification-types';
import { initI18n } from '../../../../i18n/i18n';
import { parseChangeString } from '../../../common';
import {
    checkV2Page,
    filterFlexChanges,
    findAnalyticalListPage,
    getV2Pages,
    removeEmptySettings
} from '../../import/utils';
import { MetadataInstanceFactoryV2 as Factory } from '../../export/factory';
import type { MetadataInstanceInterface } from '../../../common/decoration/factory';
import type { Definition } from 'typescript-json-schema';
import { importFlexChangesOfTableAndColumns } from './listReport';

/**
 * Adds manifest settings to the config.json.
 *
 * @param importParameters - object comprising all input data
 * @param analyticalListPageConfig - the configuration to be filled
 * @param factory - factory, for generating the access to reflect-metadata
 * @param pageKey - key of the page in manifest
 */
function addSettings(
    importParameters: ImportALPV2Parameters,
    analyticalListPageConfig: v2.AnalyticalListPageConfigV2,
    factory: MetadataInstanceInterface,
    pageKey: string
): void {
    const tableType = determineTableType(importParameters.manifest, pageKey);
    const schemaDefinitions = (importParameters.jsonSchema as Definition).definitions as Definition;

    // Import standard header actions:
    importHeaderActions(analyticalListPageConfig, factory, schemaDefinitions);

    analyticalListPageConfig.table = factory.createInstance(
        PageTypeV2.AnalyticalListPage,
        `ALP${tableType}`,
        analyticalListPageConfig.table
    );
    // Import table settings
    const lineItemsDefinition = schemaDefinitions[`ALP${tableType}<LineItems>`];
    transferSettingsOfObject(
        analyticalListPageConfig.table,
        importParameters.manifest,
        lineItemsDefinition,
        pageKey,
        importParameters.logger
    );
    // Import flex changes for columns
    importFlexChangesOfTableAndColumns(importParameters, lineItemsDefinition, analyticalListPageConfig);

    // ToolBar:
    analyticalListPageConfig.table.toolBar = factory.createInstance(
        PageTypeV2.AnalyticalListPage,
        DefinitionName.ToolBar,
        {
            actions: {}
        }
    );
    transferSettingsOfObject(
        analyticalListPageConfig.table.toolBar,
        importParameters.manifest,
        schemaDefinitions[DefinitionName.ToolBar],
        pageKey,
        importParameters.logger
    );
    if (schemaDefinitions[DefinitionName.ActionsLR]) {
        for (const actionProperty in schemaDefinitions[DefinitionName.ActionsLR].properties) {
            const actionKey = convertActionKey(actionProperty);
            analyticalListPageConfig.table.toolBar.actions[actionKey] = factory.createInstance(
                PageTypeV2.AnalyticalListPage,
                DefinitionName.Action
            );
            transferSettingsOfObject(
                analyticalListPageConfig.table.toolBar.actions[actionKey],
                importParameters.manifest,
                schemaDefinitions[DefinitionName.Action],
                pageKey,
                importParameters.logger
            );
        }
    }
    //FilterBar:
    analyticalListPageConfig.filterBar = factory.createInstance(
        PageTypeV2.AnalyticalListPage,
        DefinitionName.AnalyticalListPageFilterBar,
        analyticalListPageConfig.filterBar
    );
    transferSettingsOfObject(
        analyticalListPageConfig.filterBar,
        importParameters.manifest,
        schemaDefinitions[DefinitionName.AnalyticalListPageFilterBar],
        pageKey,
        importParameters.logger
    );
    if (schemaDefinitions[DefinitionName.SelectionFields]) {
        analyticalListPageConfig.filterBar.selectionFields = factory.createInstance(
            PageTypeV2.AnalyticalListPage,
            DefinitionName.AnalyticalListPageFilterBar,
            analyticalListPageConfig.filterBar.selectionFields
        );
        const schemaSelectionFields = schemaDefinitions[DefinitionName.SelectionFields].properties;
        for (const selectionProperty in schemaSelectionFields) {
            const selectionKey = selectionProperty;
            analyticalListPageConfig.filterBar.selectionFields[selectionKey] = factory.createInstance(
                PageTypeV2.AnalyticalListPage,
                'SelectionFieldDateSettings'
            );
            transferSettingsOfObject(
                analyticalListPageConfig.filterBar.selectionFields[selectionKey],
                importParameters.manifest,
                schemaDefinitions['SelectionFieldDateSettings'],
                pageKey,
                importParameters.logger,
                selectionKey
            );
        }
    }
    //Chart settings:
    analyticalListPageConfig.chart = factory.createInstance(
        PageTypeV2.AnalyticalListPage,
        'ChartSettings',
        analyticalListPageConfig.chart
    );
    transferSettingsOfObject(
        analyticalListPageConfig.chart,
        importParameters.manifest,
        schemaDefinitions['ChartSettings'],
        pageKey,
        importParameters.logger
    );
    //Footer:
    analyticalListPageConfig.footer = { actions: {} };
    const footerAction = schemaDefinitions[DefinitionName.FooterActionsLR];
    if (footerAction) {
        for (const actionProperty in footerAction.properties) {
            const actionKey = convertActionKey(actionProperty);
            analyticalListPageConfig.footer.actions[actionKey] = factory.createInstance(
                PageTypeV2.AnalyticalListPage,
                DefinitionName.FooterAction
            );
            transferSettingsOfObject(
                analyticalListPageConfig.footer.actions[actionKey],
                importParameters.manifest,
                schemaDefinitions[DefinitionName.Action],
                pageKey,
                importParameters.logger
            );
        }
    }

    //KPI's:
    analyticalListPageConfig.keyPerformanceIndicators = {};
    const kpiCards =
        importParameters.manifest[ManifestSection.generic]['pages'][pageKey].component?.settings
            ?.keyPerformanceIndicators;
    if (kpiCards) {
        Object.keys(kpiCards).forEach((kpiKey) => {
            analyticalListPageConfig.keyPerformanceIndicators[kpiKey] = factory.createInstance(
                PageTypeV2.AnalyticalListPage,
                'KPISettings'
            );
            analyticalListPageConfig.keyPerformanceIndicators = factory.createInstance(
                PageTypeV2.AnalyticalListPage,
                'KPISettings',
                analyticalListPageConfig.keyPerformanceIndicators
            );
            transferSettingsOfObject(
                analyticalListPageConfig.keyPerformanceIndicators[kpiKey],
                importParameters.manifest,
                schemaDefinitions['KPISettings'],
                pageKey,
                importParameters.logger,
                kpiKey
            );
        });
    }
}

/**
 * Generates the configuration file content for an analytical list page V2.
 *
 * @param importParameters - Object containing all input data.
 * @returns {v2.AnalyticalListPageConfigV2 | undefined} - The generated configuration as JSON or undefined if no pages are found.
 */
export function createAnalyticalListPageConfig(
    importParameters: ImportALPV2Parameters
): v2.AnalyticalListPageConfigV2 | undefined {
    // Initialize i18next
    initI18n();
    // Filter flex changes which are relevant only for page
    importParameters = {
        ...importParameters,
        flex: filterFlexChanges(
            importParameters.flex,
            v2.SAPUI5_VIEW_EXTENSION_ANALYTICAL_LIST_PAGE,
            importParameters.entitySet
        )
    };

    const v2Pages = getV2Pages(importParameters);
    if (!v2Pages) {
        return;
    }
    const factory = new Factory();
    //Instantiate Page
    const config = factory.createPageInstance(PageTypeV2.AnalyticalListPage);

    const pageKeys: string[] = [];
    const v2Page = findAnalyticalListPage(
        importParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
        pageKeys,
        importParameters.logger
    );
    checkV2Page(v2Page, importParameters.logger, v2Page?.entitySet);
    if (!v2Page) {
        return;
    }
    //Add the manifest settings to the config (and dependent flex changes)
    addSettings(importParameters, config, factory, pageKeys[0]);

    //Add the settings from other flex changes to the config
    importParameters.flex.forEach((change) => {
        if (parseChangeString(change)) {
            addFlex(config, change, importParameters.jsonSchema, importParameters.logger);
        }
    });
    //Add the settings from fragments to the config
    addFragments(config, importParameters, v2Page, PageTypeV2.AnalyticalListPage);

    //Remove empty properties from the config if they are not required
    removeEmptySettings(config);
    return config;
}
